import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { MessengerMessage } from '@/api/client';
import { AutoExpandScrollTextarea } from '@/components/ui/AutoExpandScrollTextarea/AutoExpandScrollTextarea';
import { SendIcon, AddIcon, ImageIcon, AttachFileIcon } from '@/components/ui/Icons';
import { MessengerComposerLayout } from '@/components/Messenger/shared/composer/MessengerComposerLayout';
import { MessageStrip } from '@/components/Messenger/shared/composer/MessageStrip';
import { MessengerComposerContextAttachBadges } from '@/components/Messenger/shared/composer/MessengerComposerContextAttachBadges';
import { MessengerComposerPendingPreviews } from '@/components/Messenger/shared/composer/MessengerComposerPendingPreviews';
import { MessengerImageSendModeModal } from '@/components/Messenger/shared/composer/MessengerImageSendModeModal';
import { MessengerMutedStatus } from '@/components/Messenger/shared/chrome/MessengerMutedStatus';
import { BulkSelectionToolbar } from '@/components/Messenger/shared/messages/BulkSelectionToolbar';
import { useTeleportOptional } from '@/contexts/TeleportContext';
import { useThreadComposer } from '@/components/Messenger/direct/useThreadComposer';
import type { useDirectThreadActions } from '@/hooks/useDirectThread';

export type DirectThreadComposerPanelHandle = {
  beginEditMessage: (msg: MessengerMessage) => void;
  setReplyTo: (msg: MessengerMessage) => void;
  clearEditingMessage: () => void;
  focusInput: () => void;
  handleDrop: React.DragEventHandler<HTMLDivElement>;
};

type SendMessage = ReturnType<typeof useDirectThreadActions>['sendMessage'];
type SendOwnAndForwardedAtomic = ReturnType<typeof useDirectThreadActions>['sendOwnAndForwardedAtomic'];

export interface DirectThreadComposerPanelProps {
  threadId: string;
  currentUserId: string | null;
  sendMessage: SendMessage;
  sendOwnAndForwardedAtomic: SendOwnAndForwardedAtomic;
  editMessage: (messageId: string, body: string) => Promise<void>;
  resolveForwardAuthorName: (msg: MessengerMessage) => string;
  getReplyToSenderName: (msg: MessengerMessage) => string;
  initialForwardMsgs?: MessengerMessage[];
  onConsumePendingForwardDraft?: () => void;
  onMessageCommitted?: (msg: MessengerMessage) => void;
  selectionMode: boolean;
  selectedIds: Set<string>;
  canDeleteCount: number;
  onBulkDelete: () => void;
  onBulkForward: () => void;
  onExitSelectionMode: () => void;
}

export const DirectThreadComposerPanel = React.memo(
  forwardRef<DirectThreadComposerPanelHandle, DirectThreadComposerPanelProps>(
    function DirectThreadComposerPanel(
      {
        threadId,
        currentUserId,
        sendMessage,
        sendOwnAndForwardedAtomic,
        editMessage,
        resolveForwardAuthorName,
        getReplyToSenderName,
        initialForwardMsgs,
        onConsumePendingForwardDraft,
        onMessageCommitted,
        selectionMode,
        selectedIds,
        canDeleteCount,
        onBulkDelete,
        onBulkForward,
        onExitSelectionMode,
      },
      ref
    ) {
      const teleport = useTeleportOptional();
      const composerInputRef = useRef<HTMLTextAreaElement>(null);

      const composer = useThreadComposer({
        threadId,
        sendMessage,
        sendOwnAndForwardedAtomic,
        resolveForwardAuthorName,
        editMessage,
        composerInputRef,
        initialForwardMsgs,
        onConsumePendingForwardDraft,
        onMessageCommitted,
      });

      useImperativeHandle(
        ref,
        () => ({
          beginEditMessage: composer.beginEditMessage,
          setReplyTo: composer.setReplyTo,
          clearEditingMessage: composer.clearEditingMessage,
          focusInput: () => composerInputRef.current?.focus(),
          handleDrop: composer.handleDrop,
        }),
        [
          composer.beginEditMessage,
          composer.setReplyTo,
          composer.clearEditingMessage,
          composer.handleDrop,
        ]
      );

      const editingMessage = composer.editingMessage;
      const clearEditingMessage = composer.clearEditingMessage;

      useEffect(() => {
        if (!editingMessage) return;
        const handler = (e: KeyboardEvent) => {
          if (e.key === 'Escape') clearEditingMessage();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
      }, [editingMessage, clearEditingMessage]);

      const previewZone =
        composer.composerFiles.length > 0 ||
        composer.attachedProjects.length > 0 ||
        composer.attachedSessions.length > 0 ? (
          <div className="chatAssistant__attachZone">
            <MessengerComposerContextAttachBadges
              attachedProjects={composer.attachedProjects}
              attachedSessions={composer.attachedSessions}
              onRemoveProject={composer.removeAttachedProject}
              onRemoveSession={composer.removeAttachedSession}
              teleport={teleport}
            />
            <MessengerComposerPendingPreviews
              items={composer.composerFiles}
              onRemove={composer.removeFile}
            />
          </div>
        ) : undefined;

      const replyStripNode = composer.editingMessage ? (
        <MessageStrip
          variant="edit"
          message={composer.editingMessage}
          senderLabel={getReplyToSenderName(composer.editingMessage)}
          onCancel={clearEditingMessage}
        />
      ) : composer.replyTo ? (
        <MessageStrip
          variant="reply"
          message={composer.replyTo}
          senderLabel={getReplyToSenderName(composer.replyTo)}
          onCancel={composer.clearReplyTo}
        />
      ) : composer.forwardMsgs.length > 0 ? (
        <MessageStrip
          variant="forward"
          messages={composer.forwardMsgs}
          getSenderName={resolveForwardAuthorName}
          onCancel={composer.clearForwardMsgs}
        />
      ) : undefined;

      const editingForSave = composer.editingMessage;
      const editSaveButtonDisabled =
        editingForSave != null
          ? (() => {
              const t = composer.input.trim();
              const prev = (editingForSave.body ?? '').trim();
              const hasMedia = Boolean(
                editingForSave.file_url && String(editingForSave.file_url).trim() !== ''
              );
              return t === prev || (!t && !hasMedia);
            })()
          : false;

      const dmComposerDisabled = currentUserId == null;

      return (
        <>
          <MessengerImageSendModeModal
            open={Boolean(composer.pendingImagePick?.length)}
            fileCount={composer.pendingImagePick?.length ?? 0}
            onClose={composer.dismissImageSendModeModal}
            onChoose={composer.handleImageSendModeChosen}
          />
          <MessengerComposerLayout
            onSubmit={
              selectionMode
                ? (e) => {
                    e.preventDefault();
                  }
                : composer.handleSubmit
            }
            previewZone={previewZone}
            replyStrip={replyStripNode}
            noticeSlot={
              !selectionMode && dmComposerDisabled ? (
                <MessengerMutedStatus>
                  Войдите в систему или обновите страницу, чтобы отправлять сообщения.
                </MessengerMutedStatus>
              ) : undefined
            }
            bulkSelectionBar={
              selectionMode ? (
                <BulkSelectionToolbar
                  selectedCount={selectedIds.size}
                  canDeleteCount={canDeleteCount}
                  onDelete={onBulkDelete}
                  onForward={onBulkForward}
                  onCancel={onExitSelectionMode}
                />
              ) : undefined
            }
            leadingSlot={
              selectionMode ? null : (
                <>
                  <input
                    ref={composer.imageFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={composer.handleImageFileSelect}
                    className="chatAssistant__fileInput"
                    aria-label="Прикрепить фото"
                  />
                  <input
                    ref={composer.anyFileInputRef}
                    type="file"
                    onChange={composer.handleAnyFileSelect}
                    className="chatAssistant__fileInput"
                    aria-label="Прикрепить файл"
                  />
                  <button
                    ref={composer.attachMenuRef}
                    type="button"
                    className="chatAssistant__attachBtn"
                    disabled={
                      dmComposerDisabled ||
                      composer.composerFiles.length > 0 ||
                      Boolean(composer.editingMessage) ||
                      composer.forwardMsgs.length > 0
                    }
                    onClick={() => composer.setAttachMenuOpen((o) => !o)}
                    title={
                      dmComposerDisabled
                        ? 'Нужна авторизация'
                        : composer.editingMessage
                          ? 'Во время редактирования вложения недоступны'
                          : composer.forwardMsgs.length > 0
                            ? 'При пересылке вложения недоступны. Отправьте или отмените пересылку.'
                            : composer.composerFiles.length > 0
                              ? 'Уже прикреплён файл. Удалите его, чтобы добавить другой.'
                              : 'Добавить'
                    }
                    aria-label="Добавить"
                    aria-expanded={composer.attachMenuOpen}
                  >
                    <AddIcon size={20} />
                  </button>
                  {composer.attachMenuShouldRender &&
                    createPortal(
                      <div
                        ref={composer.attachPopoverRef}
                        className={`context-menu ${composer.attachMenuContentClasses}`}
                        style={{ position: 'fixed', left: '-9999px', top: '-9999px' }}
                        role="menu"
                      >
                        <ul className="context-menu__list">
                          <li className="context-menu__list-item">
                            <button
                              type="button"
                              className="context-menu__item"
                              role="menuitem"
                              onMouseDown={(ev) => {
                                ev.preventDefault();
                                composer.attachMenuRequestClose();
                                composer.imageFileInputRef.current?.click();
                              }}
                            >
                              <span className="context-menu__iconCell" aria-hidden>
                                <ImageIcon size={18} />
                              </span>
                              <span className="context-menu__item-text">Добавить фото</span>
                            </button>
                          </li>
                          <li className="context-menu__list-item">
                            <button
                              type="button"
                              className="context-menu__item"
                              role="menuitem"
                              onMouseDown={(ev) => {
                                ev.preventDefault();
                                composer.attachMenuRequestClose();
                                composer.anyFileInputRef.current?.click();
                              }}
                            >
                              <span className="context-menu__iconCell" aria-hidden>
                                <AttachFileIcon size={18} />
                              </span>
                              <span className="context-menu__item-text">Прикрепить файл</span>
                            </button>
                          </li>
                        </ul>
                      </div>,
                      document.body
                    )}
                </>
              )
            }
            textarea={
              selectionMode ? null : (
                <AutoExpandScrollTextarea
                  ref={composerInputRef}
                  value={composer.input}
                  onChange={(e) => composer.handleInputChange(e.target.value)}
                  onFocus={composer.handleComposerFocus}
                  onBlur={composer.handleComposerBlur}
                  onPaste={composer.handlePaste}
                  placeholder={
                    composer.editingMessage ? 'Редактировать сообщение' : 'Введите сообщение'
                  }
                  className="chatAssistant__input form__field_inline"
                  inputClassName="chatAssistant__inputField"
                  maxVisibleLines={22}
                  fullWidth
                  disabled={dmComposerDisabled}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      const editing = composer.editingMessage;
                      const trimmedIn = composer.input.trim();
                      const hasContent = editing
                        ? !editSaveButtonDisabled
                        : Boolean(trimmedIn) ||
                          composer.composerFiles.length > 0 ||
                          composer.attachedProjects.length > 0 ||
                          composer.attachedSessions.length > 0 ||
                          composer.forwardMsgs.length > 0;
                      if (hasContent) {
                        e.preventDefault();
                        void composer.handleSubmit(e as unknown as React.FormEvent);
                      }
                    }
                  }}
                />
              )
            }
            trailingSlot={
              selectionMode ? null : (
                <button
                  type="submit"
                  disabled={
                    dmComposerDisabled ||
                    (composer.editingMessage
                      ? editSaveButtonDisabled
                      : !composer.input.trim() &&
                        composer.composerFiles.length === 0 &&
                        !composer.attachedProjects.length &&
                        !composer.attachedSessions.length &&
                        composer.forwardMsgs.length === 0)
                  }
                  className="chatAssistant__sendBtn"
                  title={composer.editingMessage ? 'Сохранить' : 'Отправить'}
                >
                  <SendIcon size={20} />
                </button>
              )
            }
          />
        </>
      );
    }
  )
);
